import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormControl, AbstractControl, ValidationErrors } from "@angular/forms";
import { DateService } from "../../services/date.service";

@Component({
    selector: 'common-date-picker',
    templateUrl: './common-date-picker.component.html',
    styleUrls: ['./common-date-picker.component.scss'],
    providers: [DateService],
    standalone: false
})
export class CommonDatePickerComponent implements OnInit, OnChanges {
	@ViewChild('datePickerInput') datePickerInput!: ElementRef<HTMLInputElement>;
	_initialDate: Date | undefined | null = null;
    _daysFutureLimit?: number | undefined | null = 0;
    _daysPastLimit?: number | undefined | null = 0;
    _futureDateLimit: Date | undefined | null;
    _pastDateLimit: Date | undefined | null;
	@Input() id: string = "id";
    @Input() alignRight: boolean = true;
    @Input() controlName: string = '';
    @Output() dateUpdated: EventEmitter<Date | null> = new EventEmitter<Date | null>();
    @Output() controlUpdated: EventEmitter<any> = new EventEmitter<any>();

    get daysFuture(): number | undefined | null {
        return this._daysFutureLimit;
    }

    @Input() set daysFuture(value: number | undefined | null) {
		this._daysFutureLimit = value;
		if (value != null) {
			this._futureDateLimit = this._initialDate == null ? new Date() : new Date(this._initialDate);
			this._futureDateLimit.setDate(this._futureDateLimit.getDate() + value);
		}
        //this.setOptions();
    }

    get daysPast(): number | null | undefined {
        return this._daysPastLimit;
    }

    @Input() set daysPast(value: number | undefined) {
		this._daysPastLimit = value;
		if (value != null) {
			this._pastDateLimit = this._initialDate == null ? new Date() : new Date(this._initialDate);
			this._pastDateLimit.setDate(this._pastDateLimit.getDate() - value);
		}
        //this.setOptions();
    }

	@Input() clearable: boolean = true;
	@Input() disabled: boolean = false;

	private _formGroup: FormGroup = new FormGroup({ datePicker: new FormControl("") });

	@Input() set formGroup(value: FormGroup) {
		this._formGroup = value;
		let ctrlValue = this._formGroup?.get('datePicker')?.value;
		if (ctrlValue != null) {
			this.setCtrl(ctrlValue);
		}
		this.addDateValidator();
	}

	get formGroup(): FormGroup {
		return this._formGroup;
	}

    get futureDateLimit(): Date | undefined | null {
        return this._futureDateLimit;
    }

    @Input() set futureDateLimit(future: Date) {
        this._futureDateLimit = future;
        //this.setOptions();
	}

	get initialDate(): Date | undefined | null {
		return this._initialDate;
	}

	@Input() set initialDate(initial: Date | undefined | null) {
		this._initialDate = initial;
	};

    get pastDateLimit(): Date | undefined | null {
        return this._pastDateLimit;
    }

    @Input() set pastDateLimit(past: Date) {
        this._pastDateLimit = past;
    }


    @Input() placeholder: string = "MM/dd/yyyy";

    constructor(private dateService: DateService) { 


    }

    ngOnInit() {
        if (this.initialDate != null) {
            this.setCtrl(this.initialDate);
        }
        if (this.disabled) {
            let ctrl = this.formGroup.get('datePicker');
            if (ctrl != null) {
                ctrl.disable();
            }
        }
        this.addDateValidator();
    }

    private dateValidator = (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (value == null || value === '') return null;
        if (value instanceof Date && !isNaN(value.getTime()) && this.filterDates(value)) return null;
        return { invalidDate: true };
    };

    private addDateValidator(): void {
        const ctrl = this._formGroup?.get('datePicker');
        if (ctrl != null && !ctrl.hasValidator(this.dateValidator)) {
            ctrl.addValidators(this.dateValidator);
            ctrl.updateValueAndValidity();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["disabled"]) {
            let value = changes["disabled"].currentValue;
            let ctrl = this.formGroup.get("datePicker");
            if (ctrl != null) {
                if (value) {
                    ctrl.disable();
                    this.setCtrl(null);
                } else {
                    ctrl.enable();
                }
            }
        }
	}

	onBlur(evt: any): void {
		let ctrl = this.formGroup.get('datePicker');
		if (ctrl != null) {
			const value = ctrl.value;
			const rawText: string = evt?.target?.value ?? '';
			const isFullDate = rawText === '' || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(rawText);
			const isValid = value == null || (isFullDate && value instanceof Date && !isNaN(value.getTime()) && this.filterDates(value));
            if (!isValid) {
				this.setCtrl(null);
				this.dateUpdated.emit(null);
				this.controlUpdated.emit({ name: this.controlName, date: null });
			} else {
				this.dateUpdated.emit(value);
				this.controlUpdated.emit({ name: this.controlName, date: value });
			}
		}
	}

	onSelectedDateChanged(obj: any): void {
		if (obj != null && obj.value != null) {
			const value = obj.value;
			const rawText: string = this.datePickerInput?.nativeElement?.value ?? '';
			const isFullDate = rawText === '' || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(rawText);
			const isValid = isFullDate && value instanceof Date && !isNaN(value.getTime()) && this.filterDates(value);
			if (!isValid) {
				this.setCtrl(null);
				this.dateUpdated.emit(null);
				this.controlUpdated.emit({ name: this.controlName, date: null });
			} else {
				this.dateUpdated.emit(value);
				this.controlUpdated.emit({ name: this.controlName, date: value });
			}
		}
	}


    public filterDates = (d: Date | null): boolean  => {
        const normalize = (date: Date) => { const n = new Date(date); n.setHours(0, 0, 0, 0); return n; };
        const day = normalize(d || new Date());
        if (this._pastDateLimit == null && this._futureDateLimit == null) return true;
        const pastLimit = this._pastDateLimit ? normalize(this._pastDateLimit) : null;
        const futureLimit = this._futureDateLimit ? normalize(this._futureDateLimit) : null;
        if (pastLimit != null && futureLimit == null) return day >= pastLimit;
        if (pastLimit == null && futureLimit != null) return day <= futureLimit;
        if (pastLimit != null && futureLimit != null) return day >= pastLimit && day <= futureLimit;
        return false;
    }

    clearDate(): void {
		let ctrl = this.formGroup.get('datePicker');
		if (ctrl != null && ctrl.enabled) {
            this.setCtrl(null);
            this.dateUpdated.emit(null);
            this.controlUpdated.emit({ name: this.controlName, date: null });
        }
     }

    setCtrl(date: Date | null): void {
        let ctrl = this.formGroup.get("datePicker");
        if (ctrl != null) ctrl.patchValue(date);
    }
}
